import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScanLine, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { endpoints } from "../lib/api";

export default function CheckinChart() {
  const [series, setSeries] = useState(null);
  const [hoverIdx, setHoverIdx] = useState(null);

  useEffect(() => {
    endpoints.attendanceSeries(7)
      .then(r => setSeries(r.data.series))
      .catch(() => setSeries([]));
  }, []);

  if (series === null) {
    return (
      <section className="mb-10 border border-border rounded-xl p-5 bg-card" data-testid="checkin-chart-section">
        <div className="h-40 animate-pulse bg-muted/40 rounded-lg" />
      </section>
    );
  }

  const total = series.reduce((s, d) => s + d.count, 0);
  const max = Math.max(1, ...series.map(d => d.count));
  const avg = series.length ? total / series.length : 0;
  const today = series[series.length - 1]?.count ?? 0;
  const yesterday = series[series.length - 2]?.count ?? 0;
  const delta = today - yesterday;
  const deltaPct = yesterday > 0 ? Math.round((delta / yesterday) * 100) : (today > 0 ? 100 : 0);
  const TrendIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const trendTone = delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-500" : "text-muted-foreground";

  // SVG geometry
  const W = 700, H = 180;
  const padX = 28, padTop = 16, padBottom = 30;
  const usableW = W - padX * 2;
  const usableH = H - padTop - padBottom;
  const barW = (usableW / series.length) * 0.6;
  const gap = (usableW / series.length) * 0.4;
  const yFor = (v) => padTop + usableH - (v / max) * usableH;

  return (
    <section className="mb-10" data-testid="checkin-chart-section">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-foreground flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-emerald-500" />
            Check-ins - last 7 days
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            {total} total · {avg.toFixed(1)} / day avg
          </p>
        </div>
        <Link
          to="/checkin#today"
          data-testid="checkin-chart-view-today"
          className="text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          View today's list →
        </Link>
      </div>

      <div className="border border-border rounded-xl p-5 bg-card">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-48 overflow-visible"
          data-testid="checkin-chart-svg"
          role="img"
          aria-label={`Bar chart of check-ins over the last ${series.length} days`}
        >
          <defs>
            <linearGradient id="bar-primary" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="1" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.55" />
            </linearGradient>
            <linearGradient id="bar-muted" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.45" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.18" />
            </linearGradient>
          </defs>

          {/* gridlines */}
          {[0.25, 0.5, 0.75, 1].map((f) => {
            const y = padTop + usableH - usableH * f;
            return (
              <line
                key={f}
                x1={padX}
                x2={W - padX}
                y1={y}
                y2={y}
                stroke="hsl(var(--border))"
                strokeDasharray="3 4"
                strokeWidth="1"
                opacity="0.5"
              />
            );
          })}

          {/* avg line */}
          {avg > 0 && (
            <line
              x1={padX}
              x2={W - padX}
              y1={yFor(avg)}
              y2={yFor(avg)}
              stroke="hsl(var(--primary))"
              strokeDasharray="2 3"
              strokeWidth="1.2"
              opacity="0.55"
            />
          )}

          {series.map((d, i) => {
            const x = padX + (usableW / series.length) * i + gap / 2;
            const y = yFor(d.count);
            const h = padTop + usableH - y;
            const isToday = i === series.length - 1;
            const isHover = hoverIdx === i;
            const fill = isToday ? "url(#bar-primary)" : "url(#bar-muted)";
            return (
              <g key={d.date}>
                {/* hit-target rect - taller for easier hover */}
                <rect
                  x={x - 4}
                  y={padTop}
                  width={barW + 8}
                  height={usableH + 10}
                  fill="transparent"
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx(null)}
                  data-testid={`chart-hit-${d.date}`}
                  style={{ cursor: "pointer" }}
                />
                {/* bar */}
                <rect
                  x={x}
                  y={y}
                  width={barW}
                  height={Math.max(h, 2)}
                  rx="4"
                  fill={fill}
                  data-testid={`chart-bar-${d.date}`}
                  style={{
                    transition: "y 400ms ease, height 400ms ease, filter 150ms",
                    filter: isHover ? "brightness(1.15)" : "none",
                  }}
                >
                  <animate
                    attributeName="height"
                    from="0"
                    to={Math.max(h, 2)}
                    dur="0.5s"
                    fill="freeze"
                    begin={`${i * 60}ms`}
                  />
                  <animate
                    attributeName="y"
                    from={padTop + usableH}
                    to={y}
                    dur="0.5s"
                    fill="freeze"
                    begin={`${i * 60}ms`}
                  />
                </rect>

                {/* value label - visible always for low bars, emphasised on today */}
                {d.count > 0 && (
                  <text
                    x={x + barW / 2}
                    y={y - 6}
                    textAnchor="middle"
                    className={isToday ? "fill-foreground" : "fill-muted-foreground"}
                    style={{ fontSize: 11, fontWeight: 600 }}
                  >
                    {d.count}
                  </text>
                )}

                {/* weekday */}
                <text
                  x={x + barW / 2}
                  y={H - 10}
                  textAnchor="middle"
                  className={isToday ? "fill-foreground" : "fill-muted-foreground"}
                  style={{ fontSize: 11, fontWeight: isToday ? 600 : 500 }}
                >
                  {d.weekday}
                </text>

                {/* hover tooltip */}
                {isHover && (
                  <g style={{ pointerEvents: "none" }}>
                    <rect
                      x={x + barW / 2 - 52}
                      y={y - 44}
                      width="104"
                      height="34"
                      rx="6"
                      className="fill-foreground"
                    />
                    <text
                      x={x + barW / 2}
                      y={y - 28}
                      textAnchor="middle"
                      className="fill-background"
                      style={{ fontSize: 10, fontWeight: 600 }}
                    >
                      {new Date(d.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                    </text>
                    <text
                      x={x + barW / 2}
                      y={y - 15}
                      textAnchor="middle"
                      className="fill-background"
                      style={{ fontSize: 11, fontWeight: 700 }}
                    >
                      {d.count} check-in{d.count === 1 ? "" : "s"}
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border flex-wrap gap-3">
          <div className="flex items-center gap-3 text-xs flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-primary" />
              <span className="text-muted-foreground">Today</span>
              <span className="font-semibold text-foreground" data-testid="chart-today-count">{today}</span>
            </div>
            <span className="text-muted-foreground/40">·</span>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-primary/40" />
              <span className="text-muted-foreground">Prior days</span>
            </div>
            <span className="text-muted-foreground/40">·</span>
            <span className={`inline-flex items-center gap-0.5 font-semibold ${trendTone}`} data-testid="chart-delta">
              <TrendIcon className="w-3 h-3" />
              {delta === 0 ? "flat" : (delta > 0 ? "+" : "") + `${deltaPct}%`} vs yesterday
            </span>
          </div>
          <div className="text-[11px] text-muted-foreground/70">
            {total === 0 ? "No check-ins yet this week." : `Avg ${avg.toFixed(1)}/day (dashed line)`}
          </div>
        </div>
      </div>
    </section>
  );
}
